import moment from 'moment-timezone';
import { WithTranslation } from 'react-i18next';
import { createSelector } from 'reselect';

import { ReduxState } from 'ducks';
import { getGuestTypesUsedInProduct } from 'lib/util/getGuestTypesUsedInProduct';
import { isRequiredGuestType } from 'lib/util/util';
import { productInstanceIsDisabled } from 'lib/util/productInstanceHelpers';
import { ProductInstance } from 'models/productInstance';
import { GuestType, Product } from 'models/product';

export const selectCandidateProductInstances = createSelector(
  (state: ReduxState) => state.productInstances.all,
  (state: ReduxState) => state.server.product.product,
  (state: ReduxState, props: { date: string }) => props.date,
  (productInstances, product, date) => {
    if (!product) return [];

    const timezone = product.start_timezone;

    return getCandidateProductInstances(productInstances, timezone, date);
  }
);

export const getCandidateProductInstances = (
  productInstances: ProductInstance[],
  timezone: string,
  date: string
) => {
  if (!date) {
    return [];
  }

  return productInstances.filter((instance: ProductInstance) => {
    const m = moment.tz(instance.start_date_time_utc, timezone);
    return (
      m.format('YYYY-MM-DD') === moment(date).format('YYYY-MM-DD') &&
      !productInstanceIsDisabled(instance, timezone)
    );
  });
};

export const selectGuestTypes = createSelector(
  (state: ReduxState) => state.server.product.product,
  (state: ReduxState, { t }: { t: WithTranslation['t'] }) => t,
  (product, t) => (product ? getGuestTypesUsedInProduct(product, t) : [])
);

export const selectMinimumParticipantsRule = createSelector(
  (state: ReduxState) => state.server.product.product,
  (product) => product?.booking_widget_settings?.minimum_participant_rules ?? []
);

export const selectInitialGuestCount = createSelector(
  selectGuestTypes,
  selectMinimumParticipantsRule,
  (guestTypes, minPaxRules) => {
    const guestCount: Record<string, number> = {};
    for (const guestType of guestTypes) {
      if (!guestCount[guestType.key]) {
        const minPaxRule = minPaxRules.find((rule) => rule.unit === guestType.key);
        const minimumParticipants = minPaxRule
          ? minPaxRule.minimum_participants ?? 0
          : isRequiredGuestType(guestType)
          ? 1
          : 0;

        if (minimumParticipants > 0) {
          guestCount[guestType.key] = minimumParticipants;
        }
      }
    }

    return guestCount;
  }
);

export const getInitialGuestCount = (
  guestTypes: GuestType[],
  product: Product
): Record<string, number> => {
  const guestCount: Record<string, number> = {};

  for (const guestType of guestTypes) {
    if (!guestCount[guestType.key]) {
      const minPaxRule = product?.booking_widget_settings?.minimum_participant_rules?.find(
        (rule) => rule.unit === guestType.key
      );
      const minimumParticipants = minPaxRule
        ? minPaxRule.minimum_participants ?? 0
        : isRequiredGuestType(guestType)
        ? 1
        : 0;

      if (minimumParticipants > 0) {
        guestCount[guestType.key] = minimumParticipants;
      }
    }
  }

  return guestCount;
};
